import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Chip, Input, Skeleton } from '@koyomi/ui';
import { api } from '../../lib/api.ts';
import { useSave } from '../../lib/queries.ts';
import { useAuthStore } from '../../app/auth-store.ts';
import '../conversations/conversations.css';

interface Scenario { key: string; title: string; hint: string; message: string }
interface Turn { role: 'user' | 'assistant'; content: string }
interface Action { tool: string; input: unknown; result: { simulated?: boolean; error?: string; hint?: string; options?: unknown[] } & Record<string, unknown>; wrote: boolean }
interface RunResult { reply: string | null; actions: Action[]; handoff: { requested: boolean; reason: string | null }; usage: { model: string; fallbackUsed: boolean; cost: number }; outcome: string; quotaBlocked?: boolean; unavailableMessage?: string }

/** Modo teste (mockup 08): cenários prontos, chat como cliente, painel do que o sistema decidiu. */
export function TestModePage() {
  const { t } = useTranslation(['messaging', 'common']);
  const businessId = useAuthStore((s) => s.businessId);
  const scenarios = useQuery({ queryKey: [businessId, 'test-scenarios'], queryFn: () => api<Scenario[]>('/business/ai/test/scenarios') });
  const [scenario, setScenario] = useState('busy_slot');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [actions, setActions] = useState<(Action & { turn: number })[]>([]);
  const [last, setLast] = useState<RunResult | null>(null);
  const [text, setText] = useState('');
  const [count, setCount] = useState(0);
  // A API aceita até 30 turnos de histórico: manda só os últimos (a conversa de teste pode ser mais longa).
  const run = useSave((message: string) => api<RunResult>('/business/ai/test', { body: { scenario, history: turns.slice(-30), message } }), [], { silent: true });
  const current = scenarios.data?.find((s) => s.key === scenario);
  const reset = (key = scenario) => { setScenario(key); setTurns([]); setActions([]); setLast(null); setText(scenarios.data?.find((s) => s.key === key)?.message ?? ''); void api('/business/ai/test/reset', { method: 'POST' }).catch(() => undefined); };
  const send = () => {
    const message = text.trim();
    if (!message) return;
    const userTurn: Turn = { role: 'user', content: message };
    setTurns((tt) => [...tt, userTurn]);
    setText('');
    run.mutate(message, { onSuccess: (r) => { setLast(r); setCount((c) => c + 1); setActions((a) => [...a, ...r.actions.map((x) => ({ ...x, turn: count + 1 }))]); const reply = r.quotaBlocked ? (r.unavailableMessage ?? '') : (r.reply ?? ''); if (reply) setTurns((tt) => [...tt, { role: 'assistant', content: reply }]); } });
  };
  return (
    <div className="k-stack" style={{ marginTop: -16 }}>
      <div style={{ background: 'var(--color-invert)', color: 'var(--color-on-invert)', padding: '12px 20px', borderRadius: 'var(--radius-md)' }}><strong>{t('messaging:testMode.title')}</strong> <span style={{ opacity: 0.8 }}>{t('messaging:testMode.banner')}</span></div>
      <div className="inbox inbox--panel inbox--test">
        <aside className="inbox__list">
          <div className="inbox__list-head"><h1 className="k-page-title" style={{ fontSize: 30 }}>{t('messaging:testMode.scenarios')}</h1></div>
          <div className="inbox__items">
            {scenarios.isLoading && <div style={{ padding: '12px 16px' }}><Skeleton lines={6} /></div>}
            {(scenarios.data ?? []).filter((s) => s.key !== 'free').map((s) => (
              <div key={s.key} className={`inbox__item ${scenario === s.key ? 'inbox__item--active' : ''}`} role="button" onClick={() => reset(s.key)}><strong>{s.title}</strong><span className="inbox__preview">{s.hint}</span></div>
            ))}
            <div className="inbox__item" role="button" onClick={() => reset('free')}><span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>{t('messaging:testMode.free')}</span></div>
          </div>
        </aside>
        <section className="inbox__thread">
          <header className="inbox__thread-head"><div className="k-row"><strong style={{ fontSize: 17 }}>{current?.title ?? t('messaging:testMode.free')}</strong><span className="k-muted">{t('messaging:testMode.fictional')}{current?.message ? ` · ${current.message}` : ''}</span></div><Button onClick={() => reset()}>{t('messaging:testMode.restart')}</Button></header>
          <div className="inbox__messages">
            {turns.map((tn, i) => <div key={i} className={`bubble ${tn.role === 'user' ? 'bubble--in' : 'bubble--ai'}`}>{tn.role === 'assistant' && <div className="bubble__author">{t('messaging:conversations.ai')}</div>}{tn.content}</div>)}
            {run.isPending && <div className="bubble bubble--ai k-muted">…</div>}
          </div>
          <div className="inbox__compose"><Input placeholder={t('messaging:testMode.placeholder')} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} /><Button variant="invert" loading={run.isPending} disabled={!text.trim()} onClick={send}>{t('messaging:testMode.send')}</Button></div>
        </section>
        <aside className="inbox__side">
          <div className="k-section-label">{t('messaging:testMode.decided')}</div>
          {last?.quotaBlocked && <Card tone="warn"><span style={{ fontSize: 13 }}>{t('messaging:testMode.quotaBlocked')}</span></Card>}
          {last?.handoff.requested && <Chip tone="warn">{t('messaging:testMode.handoff', { reason: last.handoff.reason ?? '' })}</Chip>}
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('messaging:testMode.actions')}</div>
            {actions.length === 0 && <span className="k-muted">{t('messaging:testMode.noActions')}</span>}
            <div className="k-stack" style={{ gap: 6 }}>
              {actions.map((a, i) => (
                <div key={i} className="k-mono" style={{ fontSize: 12, padding: '8px 10px', borderRadius: 8, background: a.result?.error ? 'var(--color-danger-soft)' : a.result?.simulated ? 'var(--color-accent-soft)' : 'var(--color-surface-2)' }}>
                  {a.tool}({summarize(a.input)}) → {a.result?.error ? `${t('messaging:testMode.error')}: ${a.result.error}` : a.result?.options ? `${(a.result.options as unknown[]).length} opções` : a.result?.simulated ? t('messaging:testMode.simulated') : 'ok'}
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 'auto' }} className="k-muted">
            <div style={{ fontSize: 12 }}>{t('messaging:testMode.footer', { count })}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function summarize(input: unknown): string {
  if (!input || typeof input !== 'object') return '';
  return Object.entries(input as Record<string, unknown>).slice(0, 3).map(([k, v]) => `${k}=${typeof v === 'string' ? v.slice(0, 18) : Array.isArray(v) ? `[${v.length}]` : String(v)}`).join(', ');
}
